// Prüfen und Übernehmen (#517, Etappe C/D): reine Regeln, ohne Abhängigkeiten – App und Edge Function
// binden diese Datei beide ein.

// Ziel im Drive: Unterordner "Altarchiv" im Objektordner, darin die neue Ordnerstruktur (Patrick, 17.10.2026).
export const ALTARCHIV_ORDNER = 'Altarchiv';

/** "Altarchiv/02 Finanzen/Betriebskosten" → "02 Finanzen/Betriebskosten" (Auswahl zeigt die innere Struktur). */
export function innererPfad(zielPfad: string | null | undefined): string {
  const p = (zielPfad ?? '').trim().replace(/^\/+|\/+$/g, '');
  if (p === ALTARCHIV_ORDNER) return '';
  return p.startsWith(`${ALTARCHIV_ORDNER}/`) ? p.slice(ALTARCHIV_ORDNER.length + 1) : p;
}

/** Innerer Pfad → Zielpfad mit "Altarchiv/" davor. Leer = direkt in den Altarchiv-Ordner. */
export function mitAltarchiv(inner: string | null | undefined): string {
  const p = innererPfad(inner);
  return p ? `${ALTARCHIV_ORDNER}/${p}` : ALTARCHIV_ORDNER;
}

const norm = (s: string | null | undefined) => (s ?? '').trim();

/** Wurde der Vorschlag vor dem Übernehmen geändert? (Maß der Stichprobe) */
export function istKorrigiert(
  vorschlag: { neuer_name: string | null; ziel_pfad: string | null; objekt_id: string | null },
  werte: { dateiname: string; zielPfad: string; objektId: string },
): boolean {
  return norm(vorschlag.neuer_name) !== norm(werte.dateiname)
    || mitAltarchiv(vorschlag.ziel_pfad) !== mitAltarchiv(werte.zielPfad)
    || norm(vorschlag.objekt_id) !== norm(werte.objektId);
}

export const STICHPROBE_MINDEST = 50;
export const STICHPROBE_MAX_ANTEIL = 0.04; // höchstens 2 von 50

// Zweite Stichprobe für „eher sicher“ (Patrick, 18.10.2026): 30 einzeln, höchstens 2 korrigiert.
export const STICHPROBE_MITTEL_MINDEST = 30;
export const STICHPROBE_MITTEL_MAX_ANTEIL = 1 / 15;

export type MassenStufe = 'hoch' | 'mittel';

/** Massenablage erst nach bestandener Stichprobe der jeweiligen Stufe. */
export function stichprobeBestanden(s: { einzeln: number; korrigiert: number }, stufe: MassenStufe = 'hoch'): boolean {
  const [mindest, anteil] = stufe === 'hoch' ? [STICHPROBE_MINDEST, STICHPROBE_MAX_ANTEIL] : [STICHPROBE_MITTEL_MINDEST, STICHPROBE_MITTEL_MAX_ANTEIL];
  return s.einzeln >= mindest && s.korrigiert <= Math.floor(s.einzeln * anteil + 1e-9);
}

/** Datum für den Dateinamen ersetzen: "XX_XX_XXXX_…" → "TT_MM_JJJJ_…". */
export function mitDatum(name: string, iso: string): string {
  const [j, m, t] = iso.slice(0, 10).split('-');
  return name.replace(/^(XX_XX_XXXX|00_00_\d{4})_/, `${t}_${m}_${j}_`);
}

/** Datum im Namen setzen oder ersetzen ("XX_XX_XXXX_…" oder "TT_MM_JJJJ_…" → neues Datum). */
export function setzeDatumImNamen(name: string, iso: string): string {
  const [j, m, t] = iso.slice(0, 10).split('-');
  if (!j || !m || !t) return name;
  const re = /^(XX_XX_XXXX|\d{2}_\d{2}_\d{4})_/;
  return re.test(name) ? name.replace(re, `${t}_${m}_${j}_`) : `${t}_${m}_${j}_${name}`;
}

/** Jahr aus altem Ordner bzw. Dateinamen als Hinweis (letztes plausibles Jahr, 1980–heute). */
export function jahrAusText(text: string | null | undefined): number | null {
  const heute = new Date().getFullYear();
  const treffer = [...(text ?? '').matchAll(/(?:^|[^0-9])(19[89]\d|20[0-9]{2})(?=[^0-9]|$)/g)].map((m) => Number(m[1])).filter((j) => j <= heute);
  return treffer.length ? treffer[treffer.length - 1] : null;
}

const VERBOTEN = /[\\:*?"<>|]/;
const ERLAUBTE_ENDUNG = /\.(pdf|jpe?g|png|bmp|tiff?|webp|jfif|heic|msg|eml|docx?|xlsx?|xlsm|odt|ods|rtf|pptx?|csv|txt)$/i;

const MIME: Record<string, string> = {
  pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', jfif: 'image/jpeg', png: 'image/png', bmp: 'image/bmp',
  tif: 'image/tiff', tiff: 'image/tiff', webp: 'image/webp', heic: 'image/heic', msg: 'application/vnd.ms-outlook', eml: 'message/rfc822',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', doc: 'application/msword',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', xlsm: 'application/vnd.ms-excel.sheet.macroEnabled.12',
  xls: 'application/vnd.ms-excel', odt: 'application/vnd.oasis.opendocument.text', ods: 'application/vnd.oasis.opendocument.spreadsheet',
  rtf: 'application/rtf', csv: 'text/csv', txt: 'text/plain', ppt: 'application/vnd.ms-powerpoint', pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
};
/** Dateityp für den Upload aus der Endung. */
export function mimeAusName(name: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(name || '');
  return (m && MIME[m[1].toLowerCase()]) || 'application/octet-stream';
}

const ALLGEMEINE_ORDNER = new Set(['bilder', 'fotos', 'foto', 'photos', 'images', 'bilder verkleinert', 'neuer ordner', 'scans', 'unterlagen', 'sonstiges', 'kundenzugang', 'alt']);

/**
 * Sprechender Ordnername für Fotos aus dem alten Pfad: vom Ende her der erste Ordner, der nicht allgemein ist
 * ("Bilder", "Fotos" …) und kein Objekt-/Verwaltungsordner ("0002 MV …", "01 Verwaltung").
 */
export function fotoOrdnerName(alterPfad: string | null | undefined): string | null {
  const teile = (alterPfad || '').split('/').map((t) => t.trim()).filter(Boolean);
  for (let i = teile.length - 1; i >= 0; i--) {
    const t = teile[i];
    if (/^\d{2,5}(\.\d+)?\s/.test(t) || /^0\d\s/.test(t)) return null;
    if (ALLGEMEINE_ORDNER.has(t.toLowerCase())) continue;
    // Reiner Datumsordner ("01.01.2025") behält den Namen darüber: "Zählerstände 01.01.2025".
    let name = t;
    if (/^[\d.\-_ ]+$/.test(t) && i > 0 && !/^\d{2,5}(\.\d+)?\s/.test(teile[i - 1]) && !ALLGEMEINE_ORDNER.has(teile[i - 1].toLowerCase())) name = `${teile[i - 1]} ${t}`;
    const sauber = name.replace(/[\\/:*?"<>|]/g, ' ').replace(/\s+/g, ' ').trim();
    return sauber.length > 40 ? sauber.slice(0, 40).replace(/\s+\S*$/, '').trim() : sauber;
  }
  return null;
}

/** Eingaben vor dem Ablegen prüfen; liefert einen Fehlertext oder null. */
export function pruefeAblage(werte: { dateiname: string; zielPfad: string; objektId: string | null }): string | null {
  if (!werte.objektId) return 'Bitte ein Objekt wählen.';
  const name = norm(werte.dateiname);
  if (!name) return 'Bitte einen Dateinamen eingeben.';
  if (name.includes('/') || VERBOTEN.test(name)) return 'Der Dateiname enthält unzulässige Zeichen (/ \\ : * ? " < > |).';
  if (/^XX_XX_XXXX_/.test(name)) return 'Bitte das Datum im Dateinamen ergänzen (statt XX_XX_XXXX).';
  if (!ERLAUBTE_ENDUNG.test(name)) return 'Der Dateiname braucht eine gültige Endung (z. B. .pdf, .jpg, .msg).';
  if (VERBOTEN.test(werte.zielPfad)) return 'Der Ordnerpfad enthält unzulässige Zeichen.';
  return null;
}

/** "info_immo-schwede_de" (Pfadteil im persönlichen OneDrive) → "info@immo-schwede.de". */
export function onedriveNutzer(teil: string): string | null {
  const t = decodeURIComponent(teil || '').split('_').filter(Boolean);
  if (t.length < 3) return null;
  return `${t[0]}@${t.slice(1).join('.')}`;
}

/** Name ohne echtes Datum (00_00_JJJJ / 00_00_0000 bzw. alter Platzhalter XX_XX_XXXX)? */
export function ohneDatumImNamen(name: string | null | undefined): boolean {
  return /^(XX_XX_XXXX|00_00_\d{4})_/.test(name ?? '');
}

/** Objektnummer im Dateinamen tauschen ("TT_MM_JJJJ_0003_…" → "TT_MM_JJJJ_0004_…"). */
export function mitNeuerObjektnummer(name: string, nummer: string): string {
  return name.replace(/^((?:\d{2}_\d{2}_\d{4})|(?:XX_XX_XXXX))_[^_]+_/, `$1_${nummer}_`);
}
